import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConfettiEffect from './ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playPinataHitSound, playWinFanfare } from '../utils/soundEffects';

const { FiGift, FiAward, FiSmile } = FiIcons;

const CANDY_ICONS = ["🍬", "🍭", "🍫", "⭐", "🎉", "✨", "🎀", "🍪"];

const PinataGame = ({ onComplete, reward = "Super Student Badge" }) => {
  const [hits, setHits] = useState(0);
  const maxHits = 4;
  const [isBroken, setIsBroken] = useState(false);
  const [candies, setCandies] = useState([]);
  const [wobbleKey, setWobbleKey] = useState(0);

  const handleHit = () => {
    if (isBroken) return;

    const nextHits = hits + 1;
    setHits(nextHits);
    setWobbleKey(prev => prev + 1);
    playPinataHitSound();

    // Spawn falling candies on hit
    const newCandies = Array.from({ length: 4 }).map((_, i) => ({
      id: Date.now() + i,
      icon: CANDY_ICONS[Math.floor(Math.random() * CANDY_ICONS.length)],
      x: (Math.random() - 0.5) * 160,
      y: Math.random() * 80 + 20
    }));
    setCandies(prev => [...prev.slice(-12), ...newCandies]);

    if (nextHits >= maxHits) {
      setIsBroken(true);
      playWinFanfare();
      setTimeout(() => {
        if (onComplete) onComplete(reward);
      }, 1800);
    }
  };

  const progressPercent = Math.min(100, Math.round((hits / maxHits) * 100));

  return (
    <div className="relative w-full max-w-md mx-auto flex flex-col items-center select-none py-4">
      {isBroken && <ConfettiEffect />}

      {/* Hanging Rope */}
      <div className="w-1.5 h-14 bg-amber-700/80 rounded-full mb-0 shadow-sm relative">
        <div className="absolute -bottom-2 -left-2.5 w-6 h-4 bg-amber-400 rounded-full border border-amber-600"></div>
      </div>

      {/* Interactive Piñata Mascot */}
      <div className="relative flex flex-col items-center cursor-pointer my-2" onClick={handleHit}>
        <motion.div
          key={wobbleKey}
          animate={
            isBroken
              ? { scale: [1, 1.3, 0], rotate: [0, 15, -15, 0], opacity: [1, 1, 0] }
              : hits > 0
              ? { rotate: [0, -18, 16, -10, 6, 0], scale: [1, 0.94, 1.06, 1] }
              : { rotate: [-3, 3, -3], transition: { repeat: Infinity, duration: 3, ease: "easeInOut" } }
          }
          transition={{ duration: isBroken ? 0.7 : 0.45 }}
          className="relative group"
        >
          {/* Piñata Body */}
          <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-3xl bg-gradient-to-tr from-pink-500 via-purple-500 to-amber-400 p-2 shadow-2xl flex flex-col items-center justify-center border-4 border-white/80 relative transform group-hover:scale-105 active:scale-95 transition-transform">
            
            {/* Colorful Paper Fringe Stripes */}
            <div className="absolute inset-x-2 top-3 h-4 bg-yellow-300 rounded-full opacity-90 shadow-sm"></div>
            <div className="absolute inset-x-2 top-9 h-4 bg-emerald-400 rounded-full opacity-90 shadow-sm"></div>
            <div className="absolute inset-x-2 top-16 h-4 bg-cyan-400 rounded-full opacity-90 shadow-sm"></div>
            <div className="absolute inset-x-2 top-24 h-4 bg-pink-400 rounded-full opacity-90 shadow-sm"></div>

            {/* Piñata Face */}
            <div className="relative z-10 bg-white/90 p-4 rounded-2xl shadow-md flex flex-col items-center">
              <div className="text-5xl sm:text-6xl">🪅</div>
              <div className="text-xs font-black uppercase text-purple-900 tracking-wider mt-1">
                Bash Me!
              </div>
            </div>

            {/* Streamers */}
            <div className="absolute -bottom-8 flex gap-2">
              <span className="text-2xl animate-bounce">🎀</span>
              <span className="text-2xl animate-bounce delay-100">🎊</span>
              <span className="text-2xl animate-bounce delay-200">🎈</span>
            </div>
          </div>
        </motion.div>

        {/* Flying Candies from Impact */}
        <AnimatePresence>
          {candies.map(c => (
            <motion.span
              key={c.id}
              initial={{ opacity: 1, scale: 0.8, x: 0, y: 0 }}
              animate={{ opacity: 0, scale: 1.4, x: c.x, y: c.y + 120 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute text-3xl pointer-events-none z-30"
            >
              {c.icon}
            </motion.span>
          ))}
        </AnimatePresence>
      </div>

      {/* Hit Prompt & Instructions */}
      <div className="mt-8 text-center">
        <p className="text-sm font-black uppercase tracking-wider text-purple-700 dark:text-purple-300 mb-2 flex items-center justify-center gap-1.5">
          <SafeIcon icon={FiSmile} className="text-amber-500 text-lg" />
          <span>Tap the Piñata ({maxHits - hits} hits left!)</span>
        </p>

        {/* Bash-O-Meter Progress Bar */}
        <div className="w-64 h-4 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden p-0.5 border border-purple-300 shadow-inner">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-amber-400 via-pink-500 to-purple-600"
            animate={{ width: `${progressPercent}%` }}
            transition={{ type: "spring", stiffness: 200, damping: 20 }}
          />
        </div>
      </div>

      {/* Burst Reward Reveal */}
      {isBroken && (
        <motion.div
          initial={{ scale: 0, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          className="mt-6 p-6 rounded-3xl bg-white dark:bg-slate-900 border-2 border-amber-400 shadow-2xl text-center max-w-xs w-full"
        >
          <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-3xl mx-auto mb-3 shadow-lg">
            <SafeIcon icon={FiGift} />
          </div>
          <div className="text-xs font-black uppercase tracking-wider text-pink-600 mb-1">
            🎉 Piñata Smashed!
          </div>
          <div className="text-xl font-black text-slate-900 dark:text-white">
            {reward}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Generating your prize coupon...
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PinataGame;
